import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import Layout from "../../Layout/Layout";
import { isAuthenticated } from "../../auth";
import { elakayAutoText } from "../../pageInside/dataElakay";
import { listProviderCatalogForSeller, listProviderFacets, listProviderShopsForSeller, recordProviderView } from "../services/providerProductService";
import DropshippingInterneLayout from "../components/DropshippingInterneLayout";
import ProviderSectionHeader from "../components/ProviderSectionHeader";
import ProviderFiltersPanel from "../filters/ProviderFiltersPanel";
import { countActiveFilters, filtersFromParams, filtersToParams } from "../filters/providerFilterState";
import { setDrawerFilters, clearDrawerFilters, openFilterDrawer } from "../../../components/SIdeDrawer/drawerFilterSlot";
import ProductGrid from "../ui/ProductGrid";
import ProductPagination from "../ui/ProductPagination";
import ProviderCard from "../ui/ProviderCard";
import { providerStorefrontPath } from "../providerWorkspace";
import "./DropshippingIn.css";
import "../ui/ui.css";
import { providerShopMonogram } from "../utils/providerShopIdentity";

const text = elakayAutoText;

const LIMIT = 20;
const SORTS = [["new", "h_pc_s_new", "Newest"], ["popular", "h_pc_s_pop", "Best sellers"], ["price_asc", "h_pc_s_pa", "Price: low to high"], ["price_desc", "h_pc_s_pd", "Price: high to low"], ["name", "h_pc_s_name", "Name (A-Z)"]];
const catLabel = (c) => (Array.isArray(c.name) ? c.name[0] : String(c.name || ""));

// ONE browsing screen for three contexts (same engine, same filter component):
//   mode "catalog" -- category navigation (Buyer productCatalog)
//   mode "search"  -- text search, Products | Providers tabs (Buyer resultsSearch)
//   mode "shop"    -- inside one Provider's shop: header + category chips + Filters Shop
// The general filters and the dynamic (category-driven) filters both come from the same facets
// endpoint, which is scoped by `providerId` in shop mode so only that shop's real categories,
// attributes and prices show up.
export default function ProviderCatalog({ infoSource, mode = "catalog", providerId = null }) {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const token = isAuthenticated()?.token;
  const q = params.get("q") || "";
  const sort = params.get("sort") || "new";
  const page = Math.max(1, parseInt(params.get("page"), 10) || 1);
  const tab = params.get("tab") === "providers" ? "providers" : "products";
  // The URL is the single source of truth for filters (shareable; Home, drawer and page all agree).
  const filters = useMemo(() => filtersFromParams(params), [params]);
  const [searchText, setSearchText] = useState(q);
  const [data, setData] = useState({ items: [], total: 0 });
  const [providers, setProviders] = useState({ providers: [], total: 0 });
  const [chips, setChips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const scope = useMemo(() => (providerId ? { providerId } : {}), [providerId]);
  const fkey = JSON.stringify(filters);

  const setParam = (patch) => { const n = new URLSearchParams(params); Object.entries(patch).forEach(([k, v]) => (v ? n.set(k, v) : n.delete(k))); setParams(n, { replace: true }); };
  const onFilters = (next) => setParams(filtersToParams(next, params), { replace: true });

  // Debounced text search -> URL (`q` and `setParam` are read fresh on each run; only typing re-triggers it).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { const t = setTimeout(() => searchText !== q && setParam({ q: searchText, page: "" }), 350); return () => clearTimeout(t); }, [searchText]);

  useEffect(() => {
    let live = true;
    setLoading(true);
    if (mode === "search" && tab === "providers") {
      listProviderShopsForSeller(token, { page, limit: 12, search: q, filters }).then((r) => { if (!live) return; if (r?.error) setError(r.error); else { setError(""); setProviders(r); } setLoading(false); });
    } else {
      listProviderCatalogForSeller(token, { page, limit: LIMIT, q, sort, providerId, filters }).then((r) => { if (!live) return; if (r?.error) setError(r.error); else { setError(""); setData(r); } setLoading(false); });
    }
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, sort, page, tab, fkey, providerId, mode, token]);

  // Shop mode: category chips = this Provider's real (top-level-of-what-they-sell) categories.
  useEffect(() => {
    if (mode !== "shop") return;
    let live = true;
    listProviderFacets(token, { providerId }).then((r) => live && !r?.error && setChips(r.categories || []));
    return () => { live = false; };
  }, [mode, providerId, token]);

  // Provider Views (ticket §9) -- keyed to (mode, providerId), so it fires exactly once per REAL
  // visit to this shop, never from a card impression in a list elsewhere or a plain re-render.
  useEffect(() => {
    if (mode !== "shop" || !token || !providerId) return;
    recordProviderView(token, providerId);
  }, [mode, providerId, token]);

  const shop = data.items[0]?.shop;
  const title = mode === "shop" ? (shop?.shopName || text("h_pc_shop", "Provider shop")) : mode === "search" ? text("h_pc_search", "Search results") : text("h_pc_catalog", "Provider catalog");
  const active = countActiveFilters(filters);
  // Filters live in the Filter drawer: this page registers the context it belongs to (Provider Filters,
  // or Filters Shop inside a Provider shop) and the drawer renders it.
  const onFiltersRef = useRef(onFilters);
  onFiltersRef.current = onFilters;
  const filterContext = mode === "shop" ? "providerShop" : "dropshippingIn";
  useEffect(() => {
    setDrawerFilters({ owner: "providerCatalog", context: filterContext, render: ({ click, gridButon, embedded }) => (
      <ProviderFiltersPanel mode={mode === "shop" ? "shop" : "discovery"} filters={filters} scope={scope} onChange={(n) => onFiltersRef.current(n)} embedded={embedded} click={click} gridButon={gridButon} />) });
    return () => clearDrawerFilters("providerCatalog");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fkey, scope, filterContext]);
  const empty = <><p>{q ? text("h_pc_noresult_q", "No products found for “{value0}”.", { value0: q }) : text("h_pc_noresult", "No products match your criteria.")}</p>{(active > 0 || q) && <button type="button" className="dsi-state-card__btn" onClick={() => { setSearchText(""); setParams(new URLSearchParams(providerId ? { providerId } : {}), { replace: true }); }}>{text("h_pff_clear", "Clear All")}</button>}</>;

  return (
    <Layout infoSource={infoSource} titleElakay="providerProducerIn" SliderPunShow={false} loading={false} outOffPage={false} {...infoSource?.allSettings?.layout} showFooter={true}>
      <DropshippingInterneLayout infoSource={infoSource}>
        <div className="dsi-page pcatpage">
          <nav className="dsi-breadcrumb" aria-label={text("h_c766e665183df1", "Breadcrumb")}>
            <Link to="/myShop">{text("h_spm_sell_center", "Sell Center")}</Link><span aria-hidden="true">›</span>
            <Link to="/seller/providers">{text("h_spm_title", "Providers & Producers")}</Link><span aria-hidden="true">›</span><span aria-current="page">{title}</span>
          </nav>
          {/* The Provider's storefront website (its pages) lives at /provider-shop/:id; this page is its
              full catalogue with search and filters. */}
          {mode === "shop" && shop && <ProviderSectionHeader shopName={shop.shopName} monogram={providerShopMonogram(shop)} avatar={shop.avatar} location={shop.location} totalProducts={data.total} onViewProvider={() => navigate(providerStorefrontPath(providerId))} />}
          {mode !== "shop" && <h1 className="pcatpage__title">{title}</h1>}

          <div className="pcat__toolbar">
            <input type="search" aria-label={text("h_pc_search_ph", "Search products")} placeholder={text("h_pc_search_ph", "Search products")} value={searchText} onChange={(e) => setSearchText(e.target.value)} />
            <button type="button" className="pcat__filterbtn" onClick={openFilterDrawer}>{text("h_spm_filters", "Filters")}{active ? ` (${active})` : ""}</button>
            {!(mode === "search" && tab === "providers") && <select aria-label={text("h_spm_sort", "Sort by")} value={sort} onChange={(e) => setParam({ sort: e.target.value === "new" ? "" : e.target.value, page: "" })}>{SORTS.map(([v, k, l]) => <option key={v} value={v}>{text(k, l)}</option>)}</select>}
          </div>

          {mode === "search" && <div className="pcat__tabs" role="group" aria-label={text("h_pc_results_for", "Results for")}>
            <button type="button" aria-pressed={tab === "products"} onClick={() => setParam({ tab: "", page: "" })}>{text("h_pc_tab_products", "Products")}</button>
            <button type="button" aria-pressed={tab === "providers"} onClick={() => setParam({ tab: "providers", page: "" })}>{text("h_pc_tab_providers", "Providers")}</button>
          </div>}
          {mode === "shop" && chips.length > 0 && <div className="pcat__chips" role="group" aria-label={text("h_pff_category", "Category")}>
            <button type="button" aria-pressed={!filters.categoryId} onClick={() => onFilters({ ...filters, categoryId: "", attr: [] })}>{text("h_pff_all_categories", "All categories")}</button>
            {chips.map((c) => <button type="button" key={c._id} aria-pressed={filters.categoryId === String(c._id)} onClick={() => onFilters({ ...filters, categoryId: String(c._id), attr: [] })}>{catLabel(c)} ({c.products})</button>)}
          </div>}

          <div className="pcat">
            <div>
              {error && <div className="dsi-state-card" role="alert"><h2>{text("h_spm_error_title", "Something went wrong")}</h2><p>{error}</p></div>}
              {!error && mode === "search" && tab === "providers"
                ? (providers.providers.length ? <div className="provgrid">{providers.providers.map((e) => <ProviderCard key={e.provider._id} entry={e} />)}</div> : loading ? <ProductGrid loading /> : <div className="dsi-state-card"><p>{text("h_pc_noprov", "No providers match your search.")}</p></div>)
                : !error && <><p className="wb__hint" role="status">{loading ? "" : text("h_pc_count", "{value0} products", { value0: data.total })}</p><ProductGrid products={data.items} loading={loading} empty={empty} showProvider={mode !== "shop"} /></>}
              {!error && !(mode === "search" && tab === "providers") && <ProductPagination page={page} limit={LIMIT} total={data.total} infoSource={infoSource} onPage={(n) => { setParam({ page: n > 1 ? String(n) : "" }); window.scrollTo({ top: 0 }); }} />}
            </div>
          </div>
        </div>
      </DropshippingInterneLayout>
    </Layout>
  );
}
